import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FilePlus2,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CustomPagination from "../../components/CustomPagination";
import {
  get_cms_documents,
  publish_cms_documents,
  update_cms_documents,
} from "../../reduxData/cms/cmsAction";
import {
  ConfirmModal,
  DocumentTable,
  LanguageTabs,
  PageHeader,
  TableSkeleton,
} from "./CmsDocumentComponents.js";
import { apiError, humanize, STATUSES, VISIBILITIES } from "./cmsDocumentUtils";
import "../../css/cmsDocuments.css";

const emptyFilters = { search: "", visibility: "", status: "" };

export default function DocumentsList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cmsDocuments, totalCmsDocuments } = useSelector((state) => state.cms);
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = Object.fromEntries(
    Object.keys(emptyFilters).map((key) => [key, searchParams.get(key) || ""]),
  );
  const [filters, setFilters] = useState(initial);
  const [search, setSearch] = useState(initial.search);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [language, setLanguage] = useState("EN");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = {
      page,
      limit,
      ...Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value),
      ),
    };
    setSearchParams(params, { replace: true });
    try {
      await get_cms_documents(params, dispatch);
    } catch (e) {
      setError(apiError(e, t("cmsDocuments.errors.loadDocuments")));
    } finally {
      setLoading(false);
    }
  }, [dispatch, filters, limit, page, setSearchParams]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setFilters((current) => ({ ...current, search }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  const stats = useMemo(
    () => ({
      total: totalCmsDocuments,
      published: cmsDocuments.filter(
        (document) => document.status === "PUBLISHED",
      ).length,
      draft: cmsDocuments.filter((document) => document.status === "DRAFT")
        .length,
    }),
    [cmsDocuments, totalCmsDocuments],
  );
  const data = { documents: cmsDocuments, total: totalCmsDocuments };
  const changeFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };
  const clear = () => {
    setSearch("");
    setFilters(emptyFilters);
    setPage(1);
  };
  const mutate = async (operation, success) => {
    setBusy(true);
    try {
      await operation();
      toast.success(success);
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(apiError(e, t("cmsDocuments.errors.updateDocument")));
    } finally {
      setBusy(false);
    }
  };
  const actions = {
    onPublish: (item) => setConfirm({ action: "publish", item }),
    onToggle: async (item) => {
      const activating = item.status === "ARCHIVED";
      setTogglingId(item.id);
      try {
        await update_cms_documents(
          item.id,
          {
            status: activating ? "PUBLISHED" : "ARCHIVED",
            title: item.title,
            content: item.content,
            visibility: item.visibility,
          },
          dispatch,
        );
        toast.success(
          activating
            ? t("cmsDocuments.messages.activated")
            : t("cmsDocuments.messages.deactivated"),
        );
        await load();
      } catch (e) {
        toast.error(apiError(e, t("cmsDocuments.errors.updateStatus")));
      } finally {
        setTogglingId(null);
      }
    },
  };

  return (
    <main className="cmsd-page">
      <PageHeader
        title={t("cmsDocuments.list.title")}
        description={t("cmsDocuments.list.description")}
      >
        <Link className="btn cmsd-primary" to="/cms/documents/create">
          <FilePlus2 size={18} /> {t("cmsDocuments.actions.addDocument")}
        </Link>
      </PageHeader>
      {!loading && !error && (
        <section className="cmsd-stats" aria-label={t("cmsDocuments.list.documentSummary")}>
          {Object.entries(stats).map(([key, value]) => (
            <div className="cmsd-stat" key={key}>
              <span>{t(`cmsDocuments.list.stats.${key}`)}</span>
              <strong>{value}</strong>
              <small>
                {t(key === "total" ? "cmsDocuments.list.allDocuments" : "cmsDocuments.list.loadedResult")}
              </small>
            </div>
          ))}
        </section>
      )}
      <section className="cmsd-filter-card">
        <div className="cmsd-filter-heading">
          <span>
            <SlidersHorizontal size={17} />
          </span>
          <div>
            <strong>{t("cmsDocuments.list.findDocuments")}</strong>
            <small>{t("cmsDocuments.list.filterHint")}</small>
          </div>
        </div>
        <div className="cmsd-filter-controls">
          <div className="cmsd-search">
            <Search size={17} />
            <input
              aria-label={t("cmsDocuments.list.searchDocuments")}
              placeholder={t("cmsDocuments.list.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button aria-label={t("cmsDocuments.list.clearSearch")} onClick={() => setSearch("")}>
                <X size={15} />
              </button>
            )}
          </div>
          <label>
            <span>{t("cmsDocuments.table.visibility")}</span>
            <select
              aria-label={t("cmsDocuments.table.visibility")}
              value={filters.visibility}
              onChange={(e) => changeFilter("visibility", e.target.value)}
            >
              <option value="">{t("cmsDocuments.list.allVisibility")}</option>
              {VISIBILITIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`cmsDocuments.values.${option.value.toLowerCase()}`, { defaultValue: option.label })}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t("cmsDocuments.table.status")}</span>
            <select
              aria-label={t("cmsDocuments.table.status")}
              value={filters.status}
              onChange={(e) => changeFilter("status", e.target.value)}
            >
              <option value="">{t("cmsDocuments.list.allStatuses")}</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`cmsDocuments.values.${value.toLowerCase()}`, { defaultValue: humanize(value) })}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn audit-clear-btn"
            type="button"
            onClick={clear}
            disabled={!Object.values(filters).some(Boolean) && !search}
          >
            <X size={15} /> {t("cmsDocuments.list.clearFilters")}
          </button>
        </div>
      </section>
      <div className="cmsd-list-toolbar">
        <div>
          <strong>{t("cmsDocuments.list.library")}</strong>
          <span>
            {t("cmsDocuments.list.documentCount", { count: data.total })}
          </span>
        </div>
        <LanguageTabs value={language} onChange={setLanguage} />
      </div>
      {loading ? (
        <TableSkeleton />
      ) : error ? (
        <section className="cmsd-state">
          <h2>{t("cmsDocuments.errors.loadDocumentsTitle")}</h2>
          <p>{error}</p>
          <button className="btn cmsd-primary" onClick={load}>
            <RefreshCw size={16} /> {t("cmsDocuments.actions.tryAgain")}
          </button>
        </section>
      ) : data.documents.length === 0 ? (
        <section className="cmsd-state">
          <FilePlus2 size={36} />
          <h2>
            {Object.values(filters).some(Boolean)
              ? t("cmsDocuments.list.noMatches")
              : t("cmsDocuments.list.noDocuments")}
          </h2>
          <p>
            {Object.values(filters).some(Boolean)
              ? t("cmsDocuments.list.noMatchesHint")
              : t("cmsDocuments.list.emptyHint")}
          </p>
          {Object.values(filters).some(Boolean) ? (
            <button className="btn audit-clear-btn" onClick={clear}>
              {t("cmsDocuments.list.clearFilters")}
            </button>
          ) : (
            <Link className="btn cmsd-primary" to="/cms/documents/create">
              {t("cmsDocuments.actions.addDocument")}
            </Link>
          )}
        </section>
      ) : (
        <>
          <DocumentTable
            documents={data.documents}
            actions={actions}
            language={language}
            togglingId={togglingId}
          />
          {data.total > limit && (
            <CustomPagination
              total={data.total}
              onPageChange={(nextPage, nextLimit) => {
                setPage(nextPage);
                setLimit(nextLimit);
              }}
            />
          )}
        </>
      )}
      <ConfirmModal
        action={confirm?.action}
        item={confirm?.item}
        busy={busy}
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          mutate(
            () => publish_cms_documents(confirm.item.id, dispatch),
            t("cmsDocuments.messages.published"),
          )
        }
      />
    </main>
  );
}
